import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionContext";
import { toast } from "sonner";
import { useDoxaConfirm } from "@/components/doxa/DoxaConfirmDialog";
import {
  Archive,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  Clock3,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Users,
  X,
} from "lucide-react";
import { useBusyKeys } from "@/hooks/useBusyKeys";
import { RelationSelects, isScheduled, fmtDateTime, norm } from "@/components/RelationSelects";
import { EmptyState, PageHeader, PageLoading } from "@/components/doxa/AdminSurface";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUS_OPTS = [
  { value: "active", label: "Ativos" },
  { value: "archived", label: "Arquivados" },
  { value: "all", label: "Todos" },
];

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function fmtDateOnly(value) {
  if (!value) return "";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
}

function TaskChip({ icon: Icon, children, tone = "neutral" }) {
  const tones = {
    neutral: "border-[#E7DFD3] bg-[#F6F3EE] text-[#5F574D]",
    warning: "border-[#F3D9A7] bg-[#FFF7E8] text-[#9A6518]",
  };
  return (
    <span className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold ${tones[tone] || tones.neutral}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {children}
    </span>
  );
}

function TaskRowMenu({ task, canManage, busy, onEdit, onArchive, onReactivate }) {
  if (!canManage) return null;
  const archived = task.status === "ARCHIVED";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-transparent text-[#8A8378] transition hover:border-[#F75A1B]/20 hover:bg-[#FFF0E9] hover:text-[#F75A1B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 data-[state=open]:border-[#F75A1B]/20 data-[state=open]:bg-[#FFF0E9] data-[state=open]:text-[#F75A1B]"
          aria-label={`Ações da tarefa ${task.title}`}
          data-testid={`task-row-menu-${task.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={7} className="w-60 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Ações da tarefa</span>
          <span className="mt-0.5 block truncate text-xs font-semibold normal-case tracking-normal text-[#4A4640]">{task.title}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        <DropdownMenuItem onSelect={onEdit} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`edit-task-${task.id}`}>
          <Pencil className="h-4 w-4" /> Editar tarefa
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        {archived ? (
          <DropdownMenuItem
            disabled={busy}
            onSelect={onReactivate}
            className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#2F7A45] focus:bg-[#EEF8F2] focus:text-[#2F7A45]"
            data-testid={`reactivate-task-${task.id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Reativar tarefa
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            disabled={busy}
            onSelect={onArchive}
            className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#8A5A16] focus:bg-[#FFF7E8] focus:text-[#8A5A16]"
            data-testid={`archive-task-${task.id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />} Arquivar tarefa
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Tasks() {
  const confirmAction = useDoxaConfirm();
  const { can } = usePermissions();
  const canManage = can("content.tasks");
  const { isBusy, run } = useBusyKeys();
  const [tasks, setTasks] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    return api.get("/tasks", { params: { status_filter: status } })
      .then(({ data }) => setTasks(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [status]);
  useEffect(() => { api.get("/class-groups").then(({ data }) => setClassGroups(data)); }, []);

  const cgMap = Object.fromEntries(classGroups.map((c) => [c.id, c.name]));
  const filtered = tasks.filter((t) => {
    if (!q) return true;
    return norm([t.title, t.description, cgMap[t.class_group_id], t.due_date].join(" ")).includes(norm(q));
  });

  const archive = (t) => confirmAction({
    title: "Arquivar tarefa?",
    description: <>A tarefa <strong className="font-semibold text-[#1F1F1F]">{t.title}</strong> sairá da visão de tarefas ativas e continuará disponível entre as arquivadas.</>,
    confirmLabel: "Arquivar",
    loadingLabel: "Arquivando…",
    variant: "warning",
    dataTestId: `confirm-archive-task-${t.id}`,
    onConfirm: async () => {
      await api.post(`/tasks/${t.id}/archive`);
      toast.success("Tarefa arquivada");
      await load();
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const reactivate = (t) => confirmAction({
    title: "Reativar tarefa?",
    description: <>A tarefa <strong className="font-semibold text-[#1F1F1F]">{t.title}</strong> voltará para a visão de tarefas ativas.</>,
    confirmLabel: "Reativar",
    loadingLabel: "Reativando…",
    variant: "positive",
    dataTestId: `confirm-reactivate-task-${t.id}`,
    onConfirm: async () => {
      await api.post(`/tasks/${t.id}/reactivate`);
      toast.success("Tarefa reativada");
      await load();
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const statusLabel = STATUS_OPTS.find((option) => option.value === status)?.label?.toLowerCase() || "na visão atual";

  if (loading && tasks.length === 0) return <PageLoading label="Carregando tarefas…" />;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-5 md:p-6" data-testid="tasks-page">
      <PageHeader
        eyebrow="Acadêmico"
        title="Tarefas"
        description="Organize atividades da turma, prazos e o momento em que cada tarefa passa a ficar visível."
        meta={`${tasks.length} ${tasks.length === 1 ? "tarefa" : "tarefas"} ${statusLabel}`}
        action={canManage ? (
          <button onClick={() => setEditing("new")} className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5" data-testid="new-task-btn">
            <Plus className="h-4 w-4" /> Nova tarefa
          </button>
        ) : null}
        compact
      />

      <section className="rounded-[18px] border border-[#E0D9CD] bg-white p-3.5 sm:p-4" aria-label="Filtros de tarefas">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8A8378]">Visão</p>
            <div className="inline-flex overflow-hidden rounded-xl border border-[#E0D9CD] bg-[#FAF7F1]" data-testid="task-status-filter">
              {STATUS_OPTS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatus(option.value)}
                  className={`px-3 py-2 text-xs font-semibold transition sm:px-4 ${status === option.value ? "bg-[#F75A1B] text-white shadow-sm" : "text-[#6B655E] hover:bg-[#FFF0E9] hover:text-[#D94A12]"}`}
                  data-testid={`task-status-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="min-w-0 flex-1 lg:max-w-xl">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8A8378]">Buscar</p>
            <div className="flex h-10 items-center gap-2 rounded-xl border border-[#E0D9CD] bg-white px-3 transition focus-within:border-[#F75A1B]/45 focus-within:ring-2 focus-within:ring-[#F75A1B]/10">
              <Search className="h-4 w-4 shrink-0 text-[#8A8378]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por tarefa, turma ou descrição…"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[#A59C90]"
                data-testid="tasks-search"
              />
              {q && (
                <button type="button" onClick={() => setQ("")} className="grid h-7 w-7 place-items-center rounded-lg text-[#8A8378] hover:bg-[#FFF0E9] hover:text-[#D94A12]" data-testid="tasks-search-clear" aria-label="Limpar busca">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {filtered.length > 0 ? (
        <div className="space-y-2.5" data-testid="tasks-list">
          {filtered.map((task) => {
            const archived = task.status === "ARCHIVED";
            const scheduled = isScheduled(task.starts_at);
            return (
              <article
                key={task.id}
                className="rounded-[16px] border border-[#E0D9CD] border-l-[3px] border-l-[#F75A1B] bg-white p-4 transition hover:border-[#F75A1B]/30 hover:border-l-[#F75A1B] hover:shadow-[0_10px_28px_rgba(68,53,35,0.05)] sm:p-5"
                data-testid={`task-row-${task.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#F75A1B]">Atividade</span>
                      {archived && <TaskChip icon={Archive}>Arquivada</TaskChip>}
                      {scheduled && <TaskChip icon={CalendarClock} tone="warning">Agendada</TaskChip>}
                    </div>
                    <h2 className="mt-1 font-display text-lg font-bold leading-tight text-[#171411] sm:text-xl">{task.title}</h2>
                    {task.description && <p className="mt-2 max-w-3xl text-sm leading-6 text-[#6B655E]">{task.description}</p>}
                  </div>

                  <TaskRowMenu
                    task={task}
                    canManage={canManage}
                    busy={isBusy(`${archived ? "reactivate" : "archive"}:${task.id}`)}
                    onEdit={() => setEditing(task)}
                    onArchive={() => run(`archive:${task.id}`, () => archive(task))}
                    onReactivate={() => run(`reactivate:${task.id}`, () => reactivate(task))}
                  />
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-[#F0EBE3] pt-3 text-xs text-[#6B655E]">
                  <span className="inline-flex items-center gap-1.5">
                    <Users className="h-3.5 w-3.5 text-[#F75A1B]" />
                    {cgMap[task.class_group_id] || "Turma não identificada"}
                  </span>
                  {task.due_date && (
                    <span className="inline-flex items-center gap-1.5 font-medium text-[#4A4640]">
                      <CalendarDays className="h-3.5 w-3.5 text-[#F75A1B]" />
                      Entrega {fmtDateOnly(task.due_date)}
                    </span>
                  )}
                  {task.starts_at && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock3 className="h-3.5 w-3.5 text-[#8A8378]" />
                      Visível a partir de {fmtDateTime(task.starts_at)}
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : tasks.length === 0 ? (
        <section className="rounded-[18px] border border-[#E0D9CD] bg-white">
          <EmptyState
            icon={ClipboardList}
            title={status === "archived" ? "Nenhuma tarefa arquivada" : "Nenhuma tarefa nesta visão"}
            description={status === "active" ? "Quando você criar uma tarefa, ela aparecerá aqui com turma, prazo e visibilidade organizados." : "Altere a visão para consultar outras tarefas."}
          />
          {canManage && status === "active" && (
            <div className="flex justify-center border-t border-[#F0EBE3] px-4 py-4">
              <button onClick={() => setEditing("new")} className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5">
                <Plus className="h-4 w-4" /> Criar primeira tarefa
              </button>
            </div>
          )}
        </section>
      ) : (
        <section className="rounded-[18px] border border-[#E0D9CD] bg-white">
          <EmptyState icon={Search} title="Nenhuma tarefa encontrada" description="Tente outro título, turma ou termo da descrição." />
        </section>
      )}

      {editing && (
        <TaskModal
          task={editing === "new" ? null : editing}
          classGroups={classGroups}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TaskModal({ task, classGroups, onClose, onSaved }) {
  const isEdit = !!task;
  const [form, setForm] = useState({
    title: task?.title || "",
    description: task?.description || "",
    due_date: task?.due_date || "",
    starts_at: toLocalInput(task?.starts_at),
    class_group_id: task?.class_group_id || classGroups[0]?.id || "",
    module_id: task?.module_id || "",
    lesson_id: task?.lesson_id || "",
  });
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 text-sm outline-none transition focus:border-[#F75A1B]/55 focus:ring-2 focus:ring-[#F75A1B]/10";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.class_group_id) return toast.error("Selecione a turma.");
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        due_date: form.due_date || null,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        class_group_id: form.class_group_id,
        module_id: form.module_id || null,
        lesson_id: form.lesson_id || null,
      };
      if (isEdit) await api.put(`/tasks/${task.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success(isEdit ? "Tarefa atualizada!" : "Tarefa criada!");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#241B16]/45 p-4 backdrop-blur-[2px]" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-[24px] border border-[#E0D9CD] bg-[#FFFCF8] shadow-[0_24px_80px_rgba(45,31,20,.2)]" onClick={(e) => e.stopPropagation()} data-testid="task-modal">
        <div className="flex items-start justify-between gap-4 border-b border-[#EBE5DB] bg-white px-5 py-4 sm:px-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">Acadêmico</p>
            <h2 className="mt-0.5 font-display text-xl font-bold">{isEdit ? "Editar tarefa" : "Nova tarefa"}</h2>
            <p className="mt-1 text-xs text-[#6B655E]">Defina o conteúdo, vínculo acadêmico, prazo e quando a atividade fica visível.</p>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[#8A8378] transition hover:bg-[#FFF0E9] hover:text-[#D94A12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5 p-5 sm:p-6">
          <section className="rounded-[16px] border border-[#E0D9CD] bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-display text-base font-bold">Conteúdo da tarefa</h3>
              <p className="mt-1 text-xs text-[#6B655E]">O texto que a gestão e os alunos usarão para identificar a atividade.</p>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Título</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="task-title" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Descrição</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} min-h-[96px] resize-y`} rows={3} />
              </div>
            </div>
          </section>

          <section className="rounded-[16px] border border-[#E0D9CD] bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-display text-base font-bold">Vínculo acadêmico</h3>
              <p className="mt-1 text-xs text-[#6B655E]">Associe a tarefa à turma e, se fizer sentido, a um módulo ou aula específica.</p>
            </div>
            <RelationSelects
              classGroups={classGroups}
              value={{ class_group_id: form.class_group_id, module_id: form.module_id, lesson_id: form.lesson_id }}
              onChange={(value) => setForm({ ...form, ...value })}
              prefix="task"
            />
          </section>

          <section className="rounded-[16px] border border-[#E0D9CD] bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-display text-base font-bold">Prazo e visibilidade</h3>
              <p className="mt-1 text-xs text-[#6B655E]">Entrega é opcional. O agendamento controla quando a tarefa passa a aparecer.</p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Entrega</label>
                <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} data-testid="task-due" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Visível a partir de</label>
                <input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className={inputCls} data-testid="task-starts" />
              </div>
            </div>
          </section>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} className="rounded-xl border border-[#E0D9CD] bg-white px-4 py-2.5 text-sm font-semibold text-[#5F574D] transition hover:bg-[#F8F3EA] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/20">
              Cancelar
            </button>
            <button type="submit" disabled={busy} className="doxa-btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 disabled:cursor-not-allowed disabled:opacity-60" data-testid="task-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Salvando…" : isEdit ? "Salvar alterações" : "Criar tarefa"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
